import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import SPADataTable from '../../shared/SPADataTable';
import SPAPill from '../../shared/SPAPill';
import type { SPAPillTone } from '../../shared/SPAPill';
import CollapsibleSection from './CollapsibleSection';
import { useStafferSavedWork } from './hooks';
import type {
  SavedWorkAvailability, SavedWorkSummary, SavedWorkType, SharedWorkKey, SharedWorkSummary,
} from './types';

const COLUMNS: { key: SavedWorkType; label: string }[] = [
  { key: 'assessments', label: 'Assessments' },
  { key: 'slideshows', label: 'Slideshows' },
  { key: 'questions', label: 'Questions' },
  { key: 'images', label: 'Images' },
  { key: 'highlights', label: 'Highlights' },
];

const STATUS: Record<SavedWorkAvailability, { label: string; tone: SPAPillTone }> = {
  active: { label: 'Active', tone: 'success' },
  not_active: { label: 'Not active', tone: 'neutral' },
  not_licensed: { label: 'Not licensed', tone: 'warning' },
  retired: { label: 'Retired', tone: 'error' },
  subscription_expired: { label: 'Subscription expired', tone: 'warning' },
  unavailable: { label: 'Unavailable', tone: 'error' },
};

const LEGEND: Record<SavedWorkAvailability, string> = {
  active: 'The subscriber currently holds this program, so the teacher can open this work.',
  not_active: 'The license is current but the program isn’t turned on. Re-add it from Program Access.',
  not_licensed: 'This subscriber has never licensed the program (often work brought over by a transferred teacher). '
    + 'It can’t be added without a purchase.',
  retired: 'This edition is archived or past its retirement year and can’t be added. '
    + 'The work stays in the old edition.',
  subscription_expired: 'The subscription covering this program has lapsed. Renewing restores it.',
  unavailable: 'Not offered in the subscriber’s state and not flagged as addable.',
};

// Most blocked first: orders the table rows and the legend alike.
const SEVERITY: SavedWorkAvailability[] = [
  'retired', 'unavailable', 'subscription_expired', 'not_licensed', 'not_active', 'active',
];

interface ProgramRow {
  availability: SavedWorkAvailability | null;
  counts: Partial<Record<SavedWorkType, number>>;
  id: number | null;
  title: string | null;
  total: number;
}

const toProgramRows = (summaries: SavedWorkSummary[]): ProgramRow[] => {
  const rows = new Map<string, ProgramRow>();

  summaries.forEach((summary) => {
    summary.programs.forEach((program) => {
      const key = program.id === null ? 'none' : String(program.id);
      const row = rows.get(key) ?? {
        availability: program.availability, counts: {}, id: program.id, title: program.title, total: 0,
      };

      row.counts[summary.key] = program.count;
      row.total += program.count;
      rows.set(key, row);
    });
  });

  const severity = (row: ProgramRow) => (row.availability ? SEVERITY.indexOf(row.availability) : SEVERITY.length);

  return [...rows.values()].sort((a, b) => (
    severity(a) - severity(b)
    || b.total - a.total
    || (a.title ?? '').localeCompare(b.title ?? '')
  ));
};

const columnHelper = createColumnHelper<ProgramRow>();

const eyebrowClass = 'tw-m-0 tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-tertiary';

const EmptyMark = () => <span className="tw-text-spa-tertiary">—</span>;

const StatusPill = ({ availability }: { availability: SavedWorkAvailability | null }) => {
  if (!availability) return <EmptyMark />;

  const status = STATUS[availability];

  return <SPAPill className="tw-shrink-0" tone={status.tone}>{status.label}</SPAPill>;
};

// A 1px width shrink-wraps Status and the counts to their single-token content, so the auto-width
// Program column takes all the remaining room and titles stay on one line; the footer is the Total row.
const programColumns = (totals: Record<string, number>) => [
  columnHelper.accessor('title', {
    cell: info => (info.row.original.id === null
      ? <span className="tw-italic tw-text-spa-secondary">No program</span>
      : info.getValue()),
    enableSorting: false,
    footer: 'Total',
    header: 'Program',
    meta: { width: 'auto' },
  }),
  columnHelper.accessor('availability', {
    cell: info => <StatusPill availability={info.getValue()} />,
    enableSorting: false,
    header: 'Status',
    meta: { width: 1 },
  }),
  ...COLUMNS.map(column => columnHelper.accessor(row => row.counts[column.key], {
    cell: info => info.getValue() ?? <EmptyMark />,
    enableSorting: false,
    footer: () => (
      <span className={totals[column.key] ? undefined : 'tw-text-spa-tertiary'}>{totals[column.key]}</span>
    ),
    header: column.label,
    id: column.key,
    meta: { align: 'right', width: 1 },
  })),
];

const Legend = ({ rows }: { rows: ProgramRow[] }) => {
  const present = SEVERITY.filter(state => rows.some(row => row.availability === state));

  if (present.length === 0) return null;

  return (
    <div className="tw-mt-6">
      <h4 className={eyebrowClass}>Legend</h4>
      <dl
        className={cn(
          'tw-m-0 tw-grid tw-grid-cols-[max-content_1fr] tw-items-center tw-gap-x-3 tw-gap-y-1.5',
          'tw-text-sm tw-text-spa-secondary',
        )}
      >
        {present.map(state => (
          <React.Fragment key={state}>
            <dt><StatusPill availability={state} /></dt>
            <dd className="tw-m-0">{LEGEND[state]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const SavedWorkTable = ({ summaries }: { summaries: SavedWorkSummary[] }) => {
  const rows = toProgramRows(summaries);
  const totals = Object.fromEntries(summaries.map(summary => [summary.key, summary.total]));
  const listedCount = rows.reduce((sum, row) => sum + row.total, 0);
  const distinctCount = summaries.reduce((sum, summary) => sum + summary.total, 0);

  return (
    <>
      {listedCount > distinctCount && (
        <p className="tw-mb-3 tw-mt-0 tw-text-sm tw-text-spa-secondary">
          Work on lessons that multiple programs share is listed under each of those programs —
          the Total row counts each item once.
        </p>
      )}
      <SPADataTable
        columns={programColumns(totals)}
        data={rows}
        defaultSort={[]}
        emptyMessage="No saved work."
      />
    </>
  );
};

const SHARING_LABELS: Record<SharedWorkKey, string> = {
  assessments: 'Assessments',
  questions: 'Questions',
};

const shareDescription = (summary: SharedWorkSummary) => [
  summary.district > 0 && `${summary.district} with their district only`,
  summary.everyone > 0 && `${summary.everyone} with all teachers in their state`,
].filter(Boolean).join(', ');

const SharingSummary = ({ sharing }: { sharing: SharedWorkSummary[] }) => {
  const shared = sharing.filter(summary => summary.district + summary.everyone > 0);

  if (shared.length === 0) {
    return (
      <p className="tw-mb-3 tw-mt-0 tw-text-sm tw-text-spa-secondary">
        This teacher hasn’t shared any assessments or questions to the teacher banks.
      </p>
    );
  }

  return (
    <div
      className={cn(
        'tw-mb-3 tw-box-border tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-border-solid',
        'tw-border-spa-accent tw-bg-spa-accent-bg-subtle tw-px-4 tw-py-2 tw-text-sm tw-text-spa-primary',
      )}
    >
      <span aria-hidden="true" className="tw-mt-0.5 tw-inline-flex tw-shrink-0 tw-text-spa-accent-text">
        <Icon icon="Share" size={18} />
      </span>
      <div>
        <p className="tw-m-0 tw-font-semibold tw-text-spa-heading">This teacher has shared work to the teacher banks</p>
        {shared.map(summary => (
          <p key={summary.key} className="tw-m-0">
            {SHARING_LABELS[summary.key]}: {shareDescription(summary)}
          </p>
        ))}
      </div>
    </div>
  );
};

interface SavedWorkSectionProps {
  open: boolean;
  stafferId: number;
  onOpenChange: (open: boolean) => void;
}

const SavedWorkSection = ({ open, stafferId, onOpenChange }: SavedWorkSectionProps) => {
  // Always enabled: the at-a-glance summary fetches the same query anyway, and the header
  // summary needs the counts even while the section is collapsed.
  const savedWork = useStafferSavedWork(stafferId, true);

  const total = savedWork.data?.types.reduce((sum, type) => sum + type.total, 0);
  const shared = savedWork.data?.sharing.reduce(
    (sum, summary) => sum + summary.district + summary.everyone, 0,
  );
  const headerSummary = total === undefined
    ? savedWork.isLoading && (
      <span
        aria-hidden="true"
        className="tw-inline-block tw-h-4 tw-w-16 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton tw-align-middle"
      />
    )
    : `(${total} item${total === 1 ? '' : 's'}${shared ? ` · ${shared} shared` : ''})`;

  return (
    <CollapsibleSection
      id="saved-work"
      open={open}
      summary={headerSummary}
      title="Saved work"
      onOpenChange={onOpenChange}
    >
      {savedWork.isLoading && (
        <div aria-hidden="true" className="tw-flex tw-flex-col tw-gap-3" data-testid="saved-work-skeleton">
          <div className="tw-h-4 tw-w-80 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton" />
          {[0, 1, 2, 3].map(row => (
            <div key={row} className="tw-h-6 tw-animate-pulse tw-rounded tw-bg-spa-bg-skeleton-subtle" />
          ))}
        </div>
      )}
      {savedWork.isError && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">Could not load saved work.</p>
      )}
      {savedWork.data && (
        <>
          <SharingSummary sharing={savedWork.data.sharing} />
          <SavedWorkTable summaries={savedWork.data.types} />
          <Legend rows={toProgramRows(savedWork.data.types)} />
        </>
      )}
    </CollapsibleSection>
  );
};

export default SavedWorkSection;
